import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { EmployerProfile } from '@core/models/leave-admin/employers/employer-profile.model';
import { ErrorService, LayoutService } from '@core/services';
import { EmployerProfileStoreService } from '@core/services/employer-profile-store.service';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CaseComSettingsForm, EmployerCaseComSettingsPost } from '@core/models/leave-admin/employers/case-com-settings.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { SelectionOption, StiiraError } from '@core/models';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { YesNo } from '@core/enums';

@Component({
    selector: 'app-edit-employer-case-com-settings',
    templateUrl: './edit-employer-case-com-settings.component.html',
    styleUrls: ['./edit-employer-case-com-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditEmployerCaseComSettingsComponent implements OnInit {
  static readonly componentName = 'EditEmployerCaseComSettingsComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<CaseComSettingsForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public yesNoOptions: SelectionOption[] = []; 

  public position = position;
  public onDragEnded = onDragEnded;
  public restrictToWorkEmailHint: string;

  private formInitValues: any;
  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      employerProfile: EmployerProfile,
      sysText: any
    },
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<EditEmployerCaseComSettingsComponent>,
    private store: EmployerProfileStoreService,
    private dialog: MatDialog,
    private service: ManageEmployersService,
    private errorService: ErrorService,
  ) { 
    this.form = this.fb.group<CaseComSettingsForm>({
      restrictToWorkEmail: this.fb.control(null),
      automaticCcAddresses: this.fb.control(null),
      automaticBccAddresses: this.fb.control(null)
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit() {
    const dto: EmployerCaseComSettingsPost = {
      employerId: this.data.employerProfile.employerId,

      restrictToWorkEmail:
        this.form.controls.restrictToWorkEmail.value === YesNo.Yes ? true : 
        this.form.controls.restrictToWorkEmail.value === YesNo.No ? false : 
        null,

      automaticCcAddresses: this.form.controls.automaticCcAddresses.value,
      automaticBccAddresses: this.form.controls.automaticBccAddresses.value
    };
    this.service.postEmployerCaseComSettings(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        this.store.employerProfile = res;
        this.dialogRef.close();
    },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.store.sysText.unsavedChanges,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private setupForm(): void {
    this.form.patchValue({
      restrictToWorkEmail:
        this.data.employerProfile.restrictToWorkEmail === true ? YesNo.Yes : 
        this.data.employerProfile.restrictToWorkEmail === false ? YesNo.No : 
        null,

      automaticCcAddresses: this.data.employerProfile.automaticCcAddresses,
      automaticBccAddresses: this.data.employerProfile.automaticBccAddresses
    });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.formChangeEmitted && !this.noChanges) {
          this.isEditing.emit(true);
          this.formChangeEmitted = true;
        } else if (this.noChanges) {
          this.isEditing.emit(false);
          this.formChangeEmitted = false;
        }
      });
      
    this.form.controls.restrictToWorkEmail.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.restrictToWorkEmailHint = 
        this.data.employerProfile.showRestrictToWorkEmailInherited === true && !res ?
          this.data.sysText.restrictToWorkEmailInherited : 
        this.data.employerProfile.showRestrictToWorkEmailInherited === false && !res ?
          this.data.sysText.noRestrictToWorkEmailInherited : null
      });

    this.formInitValues = { ...this.form.value };

    this.buildYesNoOptions();
  }

  private buildYesNoOptions(): void {
    const yesNo = YesNo;
    const ids = Object.values(yesNo).filter(v => typeof v === 'number');
    ids.forEach(id => {
      const desc: string = yesNo[id]
      const option: SelectionOption = {
        id: id,
        description: desc
      };
      this.yesNoOptions.push(option);
    });
  }
}